import React from "react";

import { Trans } from "@lingui/macro";

import { Add } from "@mui/icons-material";
import { Button, Divider, Typography } from "@mui/material";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import useSlackIntegration from "@app/analytics/hooks/integrations/useSlackIntegration";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import CopyLinkButton from "./CopyLinkButton";
import PushToCrm from "./PushToCrm";
import ShareToSlack from "./ShareToSlack";
import { ButtonContainer, ResponsiveMenu } from "./styles";
import type { ExternalShareProps } from "./types";
import { useProviderOptions } from "./useProviderOptions";

const ExternalShareMenu = (props: ExternalShareProps) => {
  const { open, anchorElement, onClick, onClose } = useAnchorElement();

  const { optionsInNav, optionsInMenu, options } = useProviderOptions({
    slack_bot: {
      props: {
        ...props,
        providerId: "slack_bot",
        pushIntegrationKind: "slack_bot",
      },
      checkIfProviderIsConnected: () => {
        const { isConnected } = useSlackIntegration();

        return isConnected;
      },
      Component: ShareToSlack,
    },
    hubspot: {
      props: {
        ...props,
        providerId: "hubspot",
        pushIntegrationKind: "hubspot_push",
      },
      Component: PushToCrm,
    },
    salesforce: {
      props: {
        ...props,
        providerId: "salesforce",
        pushIntegrationKind: "salesforce_push",
      },
      Component: PushToCrm,
    },
  });

  return (
    <ButtonContainer className={props.inEditMode ? "disabled" : undefined}>
      {optionsInNav.map(({ option: Option, providerId }) => (
        <Option.Component
          className="gte-md"
          key={providerId}
          {...Option.props}
        />
      ))}
      <CopyLinkButton disabled={props.inEditMode} sessionId={props.sessionId} />
      <Divider
        className={`divider${!optionsInMenu.length ? " no-options" : undefined}`}
        orientation="vertical"
        variant="middle"
        flexItem
      />
      <span onClick={onClick}>
        <HeaderButton
          className="gte-md"
          Icon={<Add />}
          sx={{
            display: !optionsInMenu.length ? "none" : undefined,
          }}
        />
        <Button
          variant="text"
          className="lt-md"
          sx={{ height: "100%", whiteSpace: "nowrap" }}
        >
          <Typography color="shades.neutral.600" variant="bodyPrimaryMed">
            <Trans>Push to...</Trans>
          </Typography>
        </Button>
      </span>
      <ResponsiveMenu
        className="gte-md"
        disableScrollLock
        open={open && !!optionsInMenu.length}
        anchorEl={anchorElement}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              padding: "5px 14px",
              minWidth: "230px",
            },
          },
        }}
      >
        {optionsInMenu.map(({ option: Option, providerId }) => (
          <Option.Component
            className="gte-md"
            key={providerId}
            isMenuItem
            {...Option?.props}
          />
        ))}
      </ResponsiveMenu>
      <ResponsiveMenu
        className="lt-md"
        disableScrollLock
        open={open}
        anchorEl={anchorElement}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              padding: "5px 14px",
              minWidth: "230px",
            },
          },
        }}
      >
        {options.map(({ option: Option, providerId }) => (
          <Option.Component key={providerId} isMenuItem {...Option?.props} />
        ))}
      </ResponsiveMenu>
    </ButtonContainer>
  );
};

export default ExternalShareMenu;
